import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function BasicArithmeticCalculator() {
  const { t } = useTranslation();
  const [num1, setNum1] = useState("");
  const [num2, setNum2] = useState("");
  const [operation, setOperation] = useState("+");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: 'https://calclab.app' },
      { name: t('categories.math'), url: 'https://calclab.app/math' },
      { name: "Basic Arithmetic Calculator", url: "https://calclab.app/calculator/basic-arithmetic" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Basic Arithmetic Calculator",
      "Add, subtract, multiply, and divide numbers easily. Simple calculator for basic math operations with instant results.",
      "https://calclab.app/calculator/basic-arithmetic",
      lang
    )
  ]);

  const calculate = () => {
    const n1 = parseFloat(num1);
    const n2 = parseFloat(num2);

    if (isNaN(n1) || isNaN(n2)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    let res: number;
    switch (operation) {
      case "+":
        res = n1 + n2;
        break;
      case "-":
        res = n1 - n2;
        break;
      case "*":
        res = n1 * n2;
        break;
      case "/":
        if (n2 === 0) {
          toast({
            title: "Error",
            description: "Cannot divide by zero.",
            variant: "destructive",
          });
          return;
        }
        res = n1 / n2;
        break;
      default:
        return;
    }

    setResult(res);
    toast({
      title: "Calculation Complete",
      description: `Result: ${res}`,
    });
  };

  const reset = () => {
    setNum1("");
    setNum2("");
    setOperation("+");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Basic Arithmetic Calculator" 
      calculatorPath="/calculator/basic-arithmetic"
      category="math"
      subcategory="Basic Math"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Basic Arithmetic Calculator</CardTitle>
            <CardDescription>
              Add, subtract, multiply, and divide numbers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="num1">First Number</Label>
                <Input
                  id="num1"
                  type="number"
                  step="any"
                  value={num1}
                  onChange={(e) => setNum1(e.target.value)}
                  placeholder="Enter first number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="operation">Operation</Label>
                <Select value={operation} onValueChange={setOperation}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="+">Addition (+)</SelectItem>
                    <SelectItem value="-">Subtraction (−)</SelectItem>
                    <SelectItem value="*">Multiplication (×)</SelectItem>
                    <SelectItem value="/">Division (÷)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="num2">Second Number</Label>
                <Input
                  id="num2"
                  type="number"
                  step="any"
                  value={num2}
                  onChange={(e) => setNum2(e.target.value)}
                  placeholder="Enter second number"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {num1} {operation} {num2} = {result}
                  </p>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
